import { IkeFieldType } from '@ikegps/core-api';

/**
 * Field type changes a form migration can carry data through. Both pairs store their values in the
 * same shape, so the data survives being moved onto the new field:
 *
 * - VECTOR -> ANCHOR: an anchor reads the vector's providers directly; `CollectionFormMigration`
 *   fills in the anchor-only wrapper fields afterwards.
 * - NUMBER -> ORIENTATION: manual, bearing and back-bearing values are identical on both sides.
 *   A number's sag provider has no orientation equivalent and is dropped during migration.
 *
 * Any other type change would need the value itself rewritten, so it is not offered as a mapping.
 */
const ALLOWED_FIELD_TYPE_CONVERSIONS: ReadonlyArray<{ from: IkeFieldType; to: IkeFieldType }> = [
	{ from: IkeFieldType.VECTOR, to: IkeFieldType.ANCHOR },
	{ from: IkeFieldType.NUMBER, to: IkeFieldType.ORIENTATION },
];

export function isAllowedFieldTypeConversion(from: IkeFieldType, to: IkeFieldType): boolean {
	return ALLOWED_FIELD_TYPE_CONVERSIONS.some((conversion) => conversion.from === from && conversion.to === to);
}

/** Whether a source field's data can migrate onto a target field of the given type. */
export function canMigrateFieldType(from: IkeFieldType, to: IkeFieldType): boolean {
	return from === to || isAllowedFieldTypeConversion(from, to);
}
